import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, ShoppingBag, Table, CheckCircle2, Copy, X, ShoppingCart, Bike, CalendarClock, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Branch, BranchOrderMethods, methodMeta } from './BranchesContext';
import { Toggle } from './Toggle';
const methodIcons: Record<keyof BranchOrderMethods, any> = {
  dineIn: QrCode,
  takeout: ShoppingCart,
  delivery: Bike,
  orderAhead: CalendarClock
};
const colorMap: Record<string, any> = {
  purple: {
    ring: 'ring-purple-200',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    iconBg: 'bg-purple-100 text-purple-600'
  },
  orange: {
    ring: 'ring-orange-200',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    iconBg: 'bg-orange-100 text-orange-600'
  },
  blue: {
    ring: 'ring-blue-200',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    iconBg: 'bg-blue-100 text-blue-600'
  },
  emerald: {
    ring: 'ring-emerald-200',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    iconBg: 'bg-emerald-100 text-emerald-600'
  }
};
export function BranchOrderingSection({
  branch,
  onUpdate
}: {
  branch: Branch;
  onUpdate: (patch: Partial<Branch>) => void;
}) {
  const [tableCount, setTableCount] = useState(10);
  const [showQRFor, setShowQRFor] = useState<{
    type: 'table' | 'takeaway';
    tableNum?: number;
  } | null>(null);
  const hasOnlineMethods = branch.methods.takeout || branch.methods.delivery || branch.methods.orderAhead;
  const hasDineIn = branch.methods.dineIn;
  const applyPreset = (preset: 'full' | 'takeout' | 'dinein') => {
    const methods = {
      ...branch.methods
    };
    if (preset === 'full') {
      methods.dineIn = true;
      methods.takeout = true;
      methods.delivery = true;
      methods.orderAhead = true;
    } else if (preset === 'takeout') {
      methods.dineIn = false;
      methods.takeout = true;
      methods.delivery = true;
      methods.orderAhead = true;
    } else if (preset === 'dinein') {
      methods.dineIn = true;
      methods.takeout = false;
      methods.delivery = false;
      methods.orderAhead = false;
    }
    onUpdate({
      methods
    });
  };
  return <div className="space-y-6" data-id="element-588">
      {/* Quick Presets */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-id="element-589">
        <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-590">Quick Presets</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-591">
          <button onClick={() => applyPreset('full')} className="p-3 rounded-xl border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 text-left transition-colors" data-id="element-592">
            <div className="font-bold text-sm text-gray-900 mb-1" data-id="element-593">
              Full Service
            </div>
            <div className="text-xs text-gray-500" data-id="element-594">
              Dine-in, Takeout, Delivery
            </div>
          </button>
          <button onClick={() => applyPreset('takeout')} className="p-3 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50 text-left transition-colors" data-id="element-595">
            <div className="font-bold text-sm text-gray-900 mb-1" data-id="element-596">
              Takeout & Delivery
            </div>
            <div className="text-xs text-gray-500" data-id="element-597">No dine-in ordering</div>
          </button>
          <button onClick={() => applyPreset('dinein')} className="p-3 rounded-xl border border-gray-200 hover:border-purple-300 hover:bg-purple-50 text-left transition-colors" data-id="element-598">
            <div className="font-bold text-sm text-gray-900 mb-1" data-id="element-599">
              Dine-in Only
            </div>
            <div className="text-xs text-gray-500" data-id="element-600">No off-premise ordering</div>
          </button>
        </div>
      </div>

      {/* Order Methods */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-id="element-601">
        <div className="mb-4" data-id="element-602">
          <h3 className="text-sm font-bold text-gray-900 mb-1" data-id="element-603">
            Order Methods
          </h3>
          <p className="text-xs text-gray-500" data-id="element-604">
            Enable or disable specific ordering channels for this location.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-605">
          {(Object.keys(branch.methods) as (keyof BranchOrderMethods)[]).map(key => {
          const enabled = branch.methods[key];
          const meta = methodMeta[key];
          const Icon = methodIcons[key];
          const colors = colorMap[meta.color];
          return <div key={key} className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${enabled ? `border-transparent ${colors.bg} ring-1 ${colors.ring}` : 'border-gray-200 bg-white'}`} data-id="element-606">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${enabled ? colors.iconBg : 'bg-gray-100 text-gray-400'}`} data-id="element-607">
                    <Icon size={16} data-id="element-608" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-609">
                    <div className="flex items-center justify-between gap-2" data-id="element-610">
                      <span className={`font-bold text-sm ${enabled ? 'text-gray-900' : 'text-gray-600'}`} data-id="element-611">
                        {meta.label}
                      </span>
                      <Toggle checked={enabled} onChange={v => onUpdate({
                  methods: {
                    ...branch.methods,
                    [key]: v
                  }
                })} data-id="element-612" />
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5 leading-snug" data-id="element-613">
                      {meta.description}
                    </p>
                  </div>
                </div>;
        })}
        </div>
      </div>

      {/* QR Codes */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-id="element-614">
        <div className="mb-4" data-id="element-615">
          <h3 className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2" data-id="element-616">
            <QrCode size={16} className="text-indigo-500" data-id="element-617" />
            QR Codes
          </h3>
          <p className="text-xs text-gray-500 leading-relaxed" data-id="element-618">
            Generate QR codes for your customers. The behavior of these codes
            depends on the order methods enabled above.
          </p>
        </div>

        <div className="space-y-6" data-id="element-619">
          {/* Takeaway QR */}
          <div data-id="element-620">
            <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-621">
              <ShoppingBag size={14} className="text-orange-500" data-id="element-622" />
              General QR
            </h4>
            {!hasOnlineMethods && <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-id="element-623">
                <strong data-id="element-624">Note:</strong> Takeout, Delivery, and Order Ahead are
                all disabled. Scanning this QR will allow customers to browse
                the menu, but they will be told to order at the counter.
              </div>}
            <div className="flex items-center justify-between p-3 rounded-xl border border-gray-200 bg-gray-50" data-id="element-625">
              <div className="text-sm font-medium text-gray-700" data-id="element-626">
                General ordering link (no table pinned)
              </div>
              <button onClick={() => setShowQRFor({
              type: 'takeaway'
            })} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-627">
                View QR
              </button>
            </div>
          </div>

          {/* Table QRs */}
          <div data-id="element-628">
            <div className="flex items-center justify-between mb-2" data-id="element-629">
              <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2" data-id="element-630">
                <Table size={14} className="text-blue-500" data-id="element-631" />
                Table QRs
              </h4>
              <div className="flex items-center gap-2" data-id="element-632">
                <span className="text-xs text-gray-500 font-medium" data-id="element-633">
                  Number of tables:
                </span>
                <input type="number" min="1" max="100" value={tableCount} onChange={e => setTableCount(parseInt(e.target.value) || 1)} className="w-16 px-2 py-1 text-xs border border-gray-200 rounded-lg text-center font-bold focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" data-id="element-634" />
              </div>
            </div>
            {!hasDineIn && <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-id="element-635">
                <strong data-id="element-636">Note:</strong> Dine-in is disabled. Scanning a Table QR
                will block the customer from ordering and tell them to order at
                the counter. This is useful if you want to prevent dine-in
                guests from placing takeout orders.
              </div>}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2" data-id="element-637">
              {Array.from({
              length: tableCount
            }).map((_, i) => {
              const tableNum = i + 1;
              return <button key={tableNum} onClick={() => setShowQRFor({
                type: 'table',
                tableNum
              })} className="flex flex-col items-center justify-center p-3 rounded-xl border border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 transition-colors group" data-id="element-638">
                    <QrCode size={20} className="text-gray-400 group-hover:text-indigo-500 mb-1.5" data-id="element-639" />
                    <span className="text-xs font-bold text-gray-700 group-hover:text-indigo-700" data-id="element-640">
                      Table {tableNum}
                    </span>
                  </button>;
            })}
            </div>
          </div>
        </div>
      </div>

      {showQRFor && <QRModal branch={branch} type={showQRFor.type} tableNum={showQRFor.tableNum} tableCount={tableCount} onChangeTable={n => setShowQRFor({
      type: 'table',
      tableNum: n
    })} onClose={() => setShowQRFor(null)} data-id="element-641" />}
    </div>;
}
function QRModal({
  branch,
  type,
  tableNum,
  tableCount = 1,
  onChangeTable,
  onClose
}: {
  branch: Branch;
  type: 'table' | 'takeaway';
  tableNum?: number;
  tableCount?: number;
  onChangeTable?: (n: number) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const query = `branch=${branch.id}&ctx=${type}` + (type === 'table' && tableNum ? `&table=${tableNum}` : '');
  const relativePath = `/menu?${query}`;
  const link = `${origin}${relativePath}`;
  const copy = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  const openPreview = () => {
    onClose();
    navigate(relativePath);
  };
  const isTable = type === 'table';
  const title = isTable ? `Table ${tableNum}` : 'General QR';
  const color = isTable ? 'blue' : 'orange';
  const canPrev = isTable && (tableNum || 1) > 1;
  const canNext = isTable && (tableNum || 1) < tableCount;
  return <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-642">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden" data-id="element-643">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100" data-id="element-644">
          <h3 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-645">
            <span className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${color}-100 text-${color}-600 flex-shrink-0`} data-id="element-646">
              {isTable ? <Table size={16} data-id="element-647" /> : <ShoppingBag size={16} data-id="element-648" />}
            </span>
            {title}
          </h3>
          <div className="flex items-center gap-1" data-id="element-649">
            {isTable && onChangeTable && <>
                <button onClick={() => canPrev && onChangeTable((tableNum || 1) - 1)} disabled={!canPrev} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Previous table" data-id="element-650">
                  <ChevronLeft size={18} data-id="element-651" />
                </button>
                <button onClick={() => canNext && onChangeTable((tableNum || 1) + 1)} disabled={!canNext} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Next table" data-id="element-652">
                  <ChevronRight size={18} data-id="element-653" />
                </button>
                <span className="w-px h-5 bg-gray-200 mx-1" data-id="element-654" />
              </>}
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Close" data-id="element-655">
              <X size={20} data-id="element-656" />
            </button>
          </div>
        </div>
        <div className="p-5 space-y-4" data-id="element-657">
          <div className={`mx-auto w-44 h-44 rounded-xl border-2 border-dashed border-${color}-300 bg-${color}-50 flex flex-col items-center justify-center text-${color}-600 gap-2`} data-id="element-658">
            <QrCode size={56} data-id="element-659" />
            <span className="text-[11px] font-semibold uppercase tracking-wide" data-id="element-660">
              Scan to order
            </span>
          </div>

          {/* In-app preview — opens the customer flow in this same session, so
            order-method changes are always reflected live. */}
          <button onClick={openPreview} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#0f172a] text-white font-bold text-sm hover:bg-[#1e293b] transition-colors" data-id="element-661">
            <ExternalLink size={16} data-id="element-662" />
            Open preview {isTable ? `(Table ${tableNum})` : ''}
          </button>

          <div data-id="element-663">
            <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-664">
              Direct Link
            </span>
            <div className="flex items-center gap-2" data-id="element-665">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 font-mono truncate select-all" data-id="element-666">
                {link}
              </div>
              <button onClick={copy} className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors flex-shrink-0" title="Copy link" data-id="element-667">
                {copied ? <CheckCircle2 size={16} className="text-emerald-500" data-id="element-668" /> : <Copy size={16} data-id="element-669" />}
              </button>
            </div>
          </div>
          <div className="text-xs text-gray-500 bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-lg leading-relaxed" data-id="element-670">
            <strong data-id="element-671">Tip:</strong> Use <strong data-id="element-672">Open preview</strong> to test live
            changes — it runs in this same session, so toggling order methods is
            reflected instantly. A copied link opened in a separate browser tab
            is a snapshot and may not pick up later changes unless it's the same
            browser/origin.
          </div>
        </div>
      </div>
    </div>;
}